import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { adminApi } from '../../services/api';
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  Globe,
  MapPin,
  Calendar,
  Clock,
  User,
  Tag,
  Edit2,
  MessageSquare,
  PhoneCall,
  Video,
  FileText,
  CheckSquare,
  Send,
  Plus,
  ExternalLink,
  MoreVertical,
  Linkedin,
  X,
  Pin
} from 'lucide-react';
import toast from 'react-hot-toast';

const tenantStatusColors = {
  trial: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800'
};

const activityIcons = {
  email: Mail,
  call: PhoneCall,
  meeting: Video,
  note: MessageSquare,
  task: CheckSquare,
  signup: User,
  login: User,
  status_change: Tag,
  plan_change: Tag,
  system: Clock
};

const activityColors = {
  email: 'bg-blue-100 text-blue-600',
  call: 'bg-green-100 text-green-600',
  meeting: 'bg-purple-100 text-purple-600',
  note: 'bg-yellow-100 text-yellow-600',
  task: 'bg-orange-100 text-orange-600',
  signup: 'bg-primary-100 text-primary-600',
  login: 'bg-gray-100 text-gray-600',
  status_change: 'bg-pink-100 text-pink-600',
  plan_change: 'bg-indigo-100 text-indigo-600',
  system: 'bg-gray-100 text-gray-500'
};

const ContactDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('activity');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityType, setActivityType] = useState('call');
  const [noteContent, setNoteContent] = useState('');
  const [activityForm, setActivityForm] = useState({
    type: 'call',
    subject: '',
    description: '',
    outcome: '',
    callType: 'outbound',
    meetingStartAt: '',
    meetingEndAt: '',
    meetingLocation: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContact();
  }, [id]);

  const fetchContact = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getMarketingContact(id);
      if (response.data.success) {
        setContact(response.data.data);
      } else {
        toast.error('Failed to load contact');
        navigate('/marketing/contacts');
      }
    } catch (err) {
      toast.error('Failed to load contact');
      console.error(err);
      navigate('/marketing/contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteContent.trim()) return;

    setSaving(true);
    try {
      await adminApi.addContactNote(id, {
        content: noteContent,
        createdByName: 'Admin'
      });
      toast.success('Note added');
      setNoteContent('');
      setShowNoteModal(false);
      fetchContact();
    } catch (err) {
      toast.error('Failed to add note');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.addContactActivity(id, {
        ...activityForm,
        performedByName: 'Admin'
      });
      toast.success('Activity logged');
      setActivityForm({
        type: 'call',
        subject: '',
        description: '',
        outcome: '',
        callType: 'outbound',
        meetingStartAt: '',
        meetingEndAt: '',
        meetingLocation: ''
      });
      setShowActivityModal(false);
      fetchContact();
    } catch (err) {
      toast.error('Failed to log activity');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Contact not found</p>
        <Link to="/marketing/contacts" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to Contacts
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/marketing/contacts')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{contact.fullName}</h1>
          <p className="text-gray-500">{contact.email}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowActivityModal(true)}
            className="btn-secondary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Log Activity
          </button>
          <button
            onClick={() => setShowNoteModal(true)}
            className="btn-primary"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Add Note
          </button>
        </div>
      </div>

      {/* Contact Card - HubSpot Style Top Bar */}
      <div className="card">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-start gap-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {contact.avatarUrl ? (
                <img
                  src={contact.avatarUrl}
                  alt={contact.fullName}
                  className="w-20 h-20 rounded-full object-cover"
                />
              ) : (
                <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center">
                  <User className="w-10 h-10 text-primary-600" />
                </div>
              )}
            </div>

            {/* Contact Info */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Column 1: Basic Info */}
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Email</p>
                  <a href={`mailto:${contact.email}`} className="text-primary-600 hover:underline flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {contact.email}
                  </a>
                </div>
                {contact.phone && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Phone</p>
                    <a href={`tel:${contact.phone}`} className="text-gray-900 flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {contact.phone}
                    </a>
                  </div>
                )}
                {contact.mobile && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Mobile</p>
                    <a href={`tel:${contact.mobile}`} className="text-gray-900 flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {contact.mobile}
                    </a>
                  </div>
                )}
              </div>

              {/* Column 2: Company Info */}
              <div className="space-y-3">
                {contact.companyName && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Company</p>
                    <p className="text-gray-900 flex items-center gap-1">
                      <Building2 className="w-4 h-4" />
                      {contact.companyName}
                    </p>
                  </div>
                )}
                {contact.jobTitle && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Job Title</p>
                    <p className="text-gray-900">{contact.jobTitle}</p>
                  </div>
                )}
                {contact.website && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Website</p>
                    <a href={contact.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline flex items-center gap-1">
                      <Globe className="w-4 h-4" />
                      {contact.website}
                    </a>
                  </div>
                )}
                {contact.linkedinUrl && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">LinkedIn</p>
                    <a href={contact.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline flex items-center gap-1">
                      <Linkedin className="w-4 h-4" />
                      View Profile
                    </a>
                  </div>
                )}
              </div>

              {/* Column 3: Tenant & Status */}
              <div className="space-y-3">
                {contact.tenant && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Linked Tenant</p>
                    <Link
                      to={`/tenants/${contact.tenant.id}`}
                      className="text-primary-600 hover:underline flex items-center gap-1"
                    >
                      <Building2 className="w-4 h-4" />
                      {contact.tenant.name}
                      <ExternalLink className="w-3 h-3" />
                    </Link>
                    <p className="text-xs text-gray-500 mt-1">practicemanagerpro.com.au/t/{contact.tenant.domain}</p>
                    {contact.tenantStatus && (
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${tenantStatusColors[contact.tenantStatus] || 'bg-gray-100 text-gray-800'}`}>
                        {contact.tenantStatus.charAt(0).toUpperCase() + contact.tenantStatus.slice(1)}
                      </span>
                    )}
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Lifecycle Stage</p>
                  <p className="text-gray-900 capitalize">{contact.lifecycleStage || 'Subscriber'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Source</p>
                  <p className="text-gray-900">{contact.source || '-'} {contact.sourceDetail && `(${contact.sourceDetail})`}</p>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="flex-shrink-0 text-right space-y-2">
              <div className="text-xs text-gray-500">
                <p>Created: {formatDate(contact.createdAt)}</p>
                {contact.lastActivityAt && (
                  <p>Last Activity: {formatTimeAgo(contact.lastActivityAt)}</p>
                )}
              </div>
              <div className="flex gap-2 text-xs">
                <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">
                  {contact.emailsSent || 0} emails sent
                </span>
                <span className="px-2 py-1 bg-green-50 text-green-700 rounded">
                  {contact.emailsOpened || 0} opened
                </span>
              </div>
              {contact.tags && contact.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 justify-end">
                  {contact.tags.map((tag, i) => (
                    <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-6 px-6">
            {['activity', 'notes', 'emails', 'calls', 'meetings'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 border-b-2 text-sm font-medium capitalize ${
                  activeTab === tab
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'activity' && (
            <div className="space-y-4">
              {(!contact.activities || contact.activities.length === 0) ? (
                <p className="text-gray-500 text-center py-8">No activities yet</p>
              ) : (
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                  {/* Activities */}
                  {contact.activities.map((activity, index) => {
                    const Icon = activityIcons[activity.type] || Clock;
                    const colorClass = activityColors[activity.type] || 'bg-gray-100 text-gray-600';

                    return (
                      <div key={activity.id || index} className="relative pl-14 pb-6">
                        {/* Icon */}
                        <div className={`absolute left-2 w-7 h-7 rounded-full flex items-center justify-center ${colorClass}`}>
                          <Icon className="w-4 h-4" />
                        </div>

                        {/* Content */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-gray-900">
                                {activity.subject || activity.type.charAt(0).toUpperCase() + activity.type.slice(1).replace('_', ' ')}
                              </p>
                              {activity.description && (
                                <p className="text-gray-600 mt-1">{activity.description}</p>
                              )}
                              {activity.outcome && (
                                <p className="text-sm text-gray-500 mt-1">
                                  <span className="font-medium">Outcome:</span> {activity.outcome}
                                </p>
                              )}
                            </div>
                            <div className="text-right text-xs text-gray-500">
                              <p>{formatDateTime(activity.activityAt)}</p>
                              {activity.performedByName && (
                                <p className="mt-1">by {activity.performedByName}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-4">
              {(!contact.notes || contact.notes.length === 0) ? (
                <p className="text-gray-500 text-center py-8">No notes yet</p>
              ) : (
                contact.notes.map((note, index) => (
                  <div key={note.id || index} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {note.isPinned && (
                          <Pin className="w-4 h-4 text-primary-600 inline mr-2" />
                        )}
                        <p className="text-gray-900 whitespace-pre-wrap">{note.content}</p>
                      </div>
                      <div className="text-right text-xs text-gray-500 ml-4">
                        <p>{formatDateTime(note.createdAt)}</p>
                        {note.createdByName && <p>by {note.createdByName}</p>}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'emails' && (
            <div className="text-center py-8 text-gray-500">
              <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Email history will appear here</p>
              <p className="text-sm">Connect your email to track communications</p>
            </div>
          )}

          {activeTab === 'calls' && (
            <div className="space-y-4">
              {contact.activities?.filter(a => a.type === 'call').length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <PhoneCall className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No calls logged yet</p>
                  <button
                    onClick={() => {
                      setActivityForm(prev => ({ ...prev, type: 'call' }));
                      setShowActivityModal(true);
                    }}
                    className="text-primary-600 hover:underline text-sm mt-2"
                  >
                    Log a call
                  </button>
                </div>
              ) : (
                contact.activities
                  .filter(a => a.type === 'call')
                  .map((call, index) => (
                    <div key={call.id || index} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{call.subject || 'Phone Call'}</p>
                          {call.description && <p className="text-gray-600 text-sm">{call.description}</p>}
                          {call.callDuration && (
                            <p className="text-xs text-gray-500 mt-1">Duration: {Math.floor(call.callDuration / 60)}m {call.callDuration % 60}s</p>
                          )}
                        </div>
                        <div className="text-right text-xs text-gray-500">
                          <p>{formatDateTime(call.activityAt)}</p>
                          <p className="capitalize">{call.callType || 'outbound'}</p>
                        </div>
                      </div>
                    </div>
                  ))
              )}
            </div>
          )}

          {activeTab === 'meetings' && (
            <div className="space-y-4">
              {contact.activities?.filter(a => a.type === 'meeting').length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Video className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No meetings scheduled</p>
                  <button
                    onClick={() => {
                      setActivityForm(prev => ({ ...prev, type: 'meeting' }));
                      setShowActivityModal(true);
                    }}
                    className="text-primary-600 hover:underline text-sm mt-2"
                  >
                    Schedule a meeting
                  </button>
                </div>
              ) : (
                contact.activities
                  .filter(a => a.type === 'meeting')
                  .map((meeting, index) => (
                    <div key={meeting.id || index} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{meeting.subject || 'Meeting'}</p>
                          {meeting.description && <p className="text-gray-600 text-sm">{meeting.description}</p>}
                          {meeting.meetingLocation && (
                            <p className="text-xs text-gray-500 mt-1">
                              <MapPin className="w-3 h-3 inline mr-1" />
                              {meeting.meetingLocation}
                            </p>
                          )}
                        </div>
                        <div className="text-right text-xs text-gray-500">
                          <p>{formatDateTime(meeting.meetingStartAt || meeting.activityAt)}</p>
                        </div>
                      </div>
                    </div>
                  ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Note</h3>
              <button onClick={() => setShowNoteModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddNote} className="p-6">
              <textarea
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                placeholder="Write your note..."
                rows={5}
                className="input"
                required
              />
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={() => setShowNoteModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="btn-primary">
                  {saving ? 'Saving...' : 'Save Note'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Activity Modal */}
      {showActivityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Log Activity</h3>
              <button onClick={() => setShowActivityModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddActivity} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Activity Type</label>
                <select
                  value={activityForm.type}
                  onChange={(e) => setActivityForm(prev => ({ ...prev, type: e.target.value }))}
                  className="input"
                >
                  <option value="call">Phone Call</option>
                  <option value="meeting">Meeting</option>
                  <option value="email">Email</option>
                  <option value="task">Task</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={activityForm.subject}
                  onChange={(e) => setActivityForm(prev => ({ ...prev, subject: e.target.value }))}
                  className="input"
                  placeholder="Brief description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
                <textarea
                  value={activityForm.description}
                  onChange={(e) => setActivityForm(prev => ({ ...prev, description: e.target.value }))}
                  className="input"
                  rows={3}
                  placeholder="Add more details..."
                />
              </div>

              {activityForm.type === 'call' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Call Type</label>
                  <select
                    value={activityForm.callType}
                    onChange={(e) => setActivityForm(prev => ({ ...prev, callType: e.target.value }))}
                    className="input"
                  >
                    <option value="outbound">Outbound</option>
                    <option value="inbound">Inbound</option>
                    <option value="missed">Missed</option>
                  </select>
                </div>
              )}

              {activityForm.type === 'meeting' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                      <input
                        type="datetime-local"
                        value={activityForm.meetingStartAt}
                        onChange={(e) => setActivityForm(prev => ({ ...prev, meetingStartAt: e.target.value }))}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                      <input
                        type="datetime-local"
                        value={activityForm.meetingEndAt}
                        onChange={(e) => setActivityForm(prev => ({ ...prev, meetingEndAt: e.target.value }))}
                        className="input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <input
                      type="text"
                      value={activityForm.meetingLocation}
                      onChange={(e) => setActivityForm(prev => ({ ...prev, meetingLocation: e.target.value }))}
                      className="input"
                      placeholder="Office, Zoom, etc."
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Outcome</label>
                <input
                  type="text"
                  value={activityForm.outcome}
                  onChange={(e) => setActivityForm(prev => ({ ...prev, outcome: e.target.value }))}
                  className="input"
                  placeholder="Result of the activity"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                <button type="button" onClick={() => setShowActivityModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="btn-primary">
                  {saving ? 'Saving...' : 'Log Activity'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactDetailPage;
